// i18n

import { useState, SyntheticEvent, useEffect } from 'react';

import { get } from 'infras/rest_client';
import { apiPath } from 'presentations/routes/path';
import { ViewerItem } from 'presentations/api/schema/user';
import { TeamUserListItem } from 'presentations/api/schema/team_user';
import { useDebounce } from 'presentations/views/hooks/debounce';

interface Props {
  viewer: ViewerItem;
  isExcludeViewer: boolean;
  selectedTeamUserIds: string[];
  isSupportString?: boolean;
  onUpdate?: any;
}

export const useAutoCompleteTeamUserField = (props: Props) => {
  const [suggestTeamUsers, setSuggestTeamUsers] = useState<TeamUserListItem[]>(
    [],
  );
  const [inputValue, setInputValue] = useState<string>('');
  const debouncedInputValue = useDebounce(inputValue, 200);

  useEffect(() => {
    (async () => {
      const findResult = await get(
        apiPath(`/v1/teams/${props.viewer.team?.uid}/users`, {
          keyword: debouncedInputValue,
          page: 1,
          perPage: 20,
        }),
      );
      if (!findResult.isSuccess) {
        return;
      }
      const teamUsers: TeamUserListItem[] = findResult.data;
      setSuggestTeamUsers(teamUsers);
    })();
  }, [debouncedInputValue]);

  // オプションを選択した時に呼ばれるcallback
  const onChangeAutoComplete = async (
    e: SyntheticEvent,
    value: { uid: string; title: string; image: string | null } | string | null,
  ) => {
    if (value) {
      if (typeof value === 'string') {
        if (props.isSupportString) {
          props.onUpdate && props.onUpdate(value);
        }
      } else {
        let newUser;
        if (suggestTeamUsers.length > 0) {
          newUser = suggestTeamUsers.find((u) => u.userId === value.uid);
        }
        if (!newUser) {
          return;
        }
        props.onUpdate && props.onUpdate(newUser);
      }
    }
  };

  // テキストフィールドの入力値に変更があった時に呼ばれるcallback
  const onInputChange = async (e: SyntheticEvent, value: string) => {
    if (e?.type === 'click') {
      // optionのclickを起因だった場合、テキストフィールドを空文字にする
      setInputValue('');
    } else {
      setInputValue(value);
    }
  };

  const teamUserOptions: {
    uid: string;
    title: string;
    image: string;
  }[] = suggestTeamUsers
    .filter((user) => {
      if (props.selectedTeamUserIds.includes(user.userId)) {
        return false;
      }
      if (props.isExcludeViewer && user.userId === props.viewer.uid) {
        return false;
      }
      return true;
    })
    .map((user) => ({
      uid: user.userId,
      title: user.name,
      image: user.profileImage ?? '',
    }));

  return {
    state: {
      teamUserOptions,
      inputValue,
    },
    actions: {
      onChangeAutoComplete,
      onInputChange,
    },
  };
};
